import type { SpectralAnalysis, TransientAnalysis } from './types.js';

export interface SpectralInput {
  samples: Float32Array;
  sample_rate: number;
}

const FFT_SIZE = 1024;
const HOP_SIZE = 512;
const OCTAVE_CENTERS = [31.25, 62.5, 125, 250, 500, 1000, 2000, 4000, 8000, 16000];

export function analyzeSpectral(input: SpectralInput): SpectralAnalysis {
  const { samples, sample_rate } = input;
  if (samples.length < FFT_SIZE) {
    return {
      centroid_hz: 0,
      rolloff_85_hz: 0,
      flatness: 0,
      tilt_db_per_octave: 0,
      octave_bands_db: new Array<number>(OCTAVE_CENTERS.length).fill(-Infinity),
    };
  }

  const window = hannWindow(FFT_SIZE);
  const halfBins = FFT_SIZE / 2;
  const magSumSq = new Float64Array(halfBins);
  let frameCount = 0;

  const re = new Float64Array(FFT_SIZE);
  const im = new Float64Array(FFT_SIZE);

  for (let start = 0; start + FFT_SIZE <= samples.length; start += HOP_SIZE) {
    for (let i = 0; i < FFT_SIZE; i++) {
      re[i] = samples[start + i] * window[i];
      im[i] = 0;
    }
    fftRadix2(re, im);
    for (let k = 0; k < halfBins; k++) {
      const mag = Math.sqrt(re[k] * re[k] + im[k] * im[k]);
      magSumSq[k] += mag * mag;
    }
    frameCount++;
  }

  if (frameCount === 0) {
    return {
      centroid_hz: 0,
      rolloff_85_hz: 0,
      flatness: 0,
      tilt_db_per_octave: 0,
      octave_bands_db: new Array<number>(OCTAVE_CENTERS.length).fill(-Infinity),
    };
  }

  const avgMag = new Float64Array(halfBins);
  for (let k = 0; k < halfBins; k++) {
    avgMag[k] = Math.sqrt(magSumSq[k] / frameCount);
  }

  const binHz = sample_rate / FFT_SIZE;

  let weighted = 0;
  let total = 0;
  for (let k = 1; k < halfBins; k++) {
    const f = k * binHz;
    weighted += f * avgMag[k];
    total += avgMag[k];
  }
  const centroid_hz = total > 0 ? weighted / total : 0;

  let energySum = 0;
  for (let k = 1; k < halfBins; k++) energySum += avgMag[k] * avgMag[k];
  const target = 0.85 * energySum;
  let cum = 0;
  let rolloff_85_hz = 0;
  for (let k = 1; k < halfBins; k++) {
    cum += avgMag[k] * avgMag[k];
    if (cum >= target) {
      rolloff_85_hz = k * binHz;
      break;
    }
  }

  // Flatness = geometric mean / arithmetic mean of |X|^2 (Wiener entropy).
  let logSum = 0;
  let arithSum = 0;
  let validBins = 0;
  for (let k = 1; k < halfBins; k++) {
    const p = avgMag[k] * avgMag[k];
    if (p > 1e-30) {
      logSum += Math.log(p);
      arithSum += p;
      validBins++;
    }
  }
  let flatness = 0;
  if (validBins > 0 && arithSum > 0) {
    const geoMean = Math.exp(logSum / validBins);
    const arithMean = arithSum / validBins;
    flatness = geoMean / arithMean;
  }

  const octave_bands_db = OCTAVE_CENTERS.map((cf) => {
    const lo = cf / Math.SQRT2;
    const hi = cf * Math.SQRT2;
    let bandPow = 0;
    for (let k = 1; k < halfBins; k++) {
      const f = k * binHz;
      if (f >= lo && f < hi) bandPow += avgMag[k] * avgMag[k];
    }
    return bandPow > 0 ? 10 * Math.log10(bandPow) : -Infinity;
  });

  // Tilt: linear regression of log10(|X|) vs log10(f) on bins in [100 Hz, 10 kHz].
  // Slope is dB-per-decade-equivalent in log10(mag); convert to dB/octave:
  //   dB/oct = 20 * slope * log10(2) ≈ 6.0206 * slope.
  const xs: number[] = [];
  const ys: number[] = [];
  for (let k = 1; k < halfBins; k++) {
    const f = k * binHz;
    if (f >= 100 && f <= 10000 && avgMag[k] > 1e-12) {
      xs.push(Math.log10(f));
      ys.push(Math.log10(avgMag[k]));
    }
  }
  let tilt_db_per_octave = 0;
  if (xs.length >= 2) {
    const n = xs.length;
    let sx = 0,
      sy = 0,
      sxy = 0,
      sxx = 0;
    for (let i = 0; i < n; i++) {
      sx += xs[i];
      sy += ys[i];
      sxy += xs[i] * ys[i];
      sxx += xs[i] * xs[i];
    }
    const denom = n * sxx - sx * sx;
    if (denom !== 0) {
      const slope = (n * sxy - sx * sy) / denom;
      tilt_db_per_octave = 20 * slope * Math.log10(2);
    }
  }

  return {
    centroid_hz,
    rolloff_85_hz,
    flatness,
    tilt_db_per_octave,
    octave_bands_db,
  };
}

export function analyzeTransients(input: SpectralInput): TransientAnalysis {
  const { samples, sample_rate } = input;
  if (samples.length < FFT_SIZE) {
    return { density_per_sec: 0, onsets: [] };
  }

  const window = hannWindow(FFT_SIZE);
  const halfBins = FFT_SIZE / 2;
  const re = new Float64Array(FFT_SIZE);
  const im = new Float64Array(FFT_SIZE);
  let prev: Float64Array | null = null;
  const flux: number[] = [];
  const frameTimes: number[] = [];

  for (let start = 0; start + FFT_SIZE <= samples.length; start += HOP_SIZE) {
    for (let i = 0; i < FFT_SIZE; i++) {
      re[i] = samples[start + i] * window[i];
      im[i] = 0;
    }
    fftRadix2(re, im);
    const mag = new Float64Array(halfBins);
    for (let k = 0; k < halfBins; k++) {
      mag[k] = Math.sqrt(re[k] * re[k] + im[k] * im[k]);
    }
    let f = 0;
    if (prev) {
      for (let k = 0; k < halfBins; k++) {
        const d = mag[k] - prev[k];
        if (d > 0) f += d;
      }
    }
    flux.push(f);
    frameTimes.push((start + FFT_SIZE / 2) / sample_rate);
    prev = mag;
  }

  if (flux.length < 3) return { density_per_sec: 0, onsets: [] };

  // Adaptive threshold: median + 3 * MAD over the whole flux series.
  const sorted = [...flux].sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)];
  const absDev = flux.map((v) => Math.abs(v - median)).sort((a, b) => a - b);
  const mad = absDev[Math.floor(absDev.length / 2)];
  const threshold = median + 3 * mad + 1e-9;

  const onsets: number[] = [];
  const minGapSec = 0.05;
  let lastOnset = -Infinity;
  for (let i = 1; i < flux.length - 1; i++) {
    if (
      flux[i] > threshold &&
      flux[i] >= flux[i - 1] &&
      flux[i] >= flux[i + 1] &&
      frameTimes[i] - lastOnset >= minGapSec
    ) {
      onsets.push(frameTimes[i]);
      lastOnset = frameTimes[i];
    }
  }

  const durSec = samples.length / sample_rate;
  const density_per_sec = durSec > 0 ? onsets.length / durSec : 0;
  return { density_per_sec, onsets };
}

function hannWindow(n: number): Float64Array {
  const w = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    w[i] = 0.5 * (1 - Math.cos((2 * Math.PI * i) / (n - 1)));
  }
  return w;
}

// In-place Cooley-Tukey radix-2 FFT. n must be a power of 2.
function fftRadix2(re: Float64Array, im: Float64Array): void {
  const n = re.length;
  let j = 0;
  for (let i = 1; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) {
      const tr = re[i];
      re[i] = re[j];
      re[j] = tr;
      const ti = im[i];
      im[i] = im[j];
      im[j] = ti;
    }
  }
  for (let size = 2; size <= n; size <<= 1) {
    const half = size >> 1;
    const theta = (-2 * Math.PI) / size;
    const wpr = Math.cos(theta);
    const wpi = Math.sin(theta);
    for (let start = 0; start < n; start += size) {
      let wr = 1;
      let wi = 0;
      for (let k = 0; k < half; k++) {
        const i0 = start + k;
        const i1 = i0 + half;
        const tr = wr * re[i1] - wi * im[i1];
        const ti = wr * im[i1] + wi * re[i1];
        re[i1] = re[i0] - tr;
        im[i1] = im[i0] - ti;
        re[i0] = re[i0] + tr;
        im[i0] = im[i0] + ti;
        const nwr = wr * wpr - wi * wpi;
        wi = wr * wpi + wi * wpr;
        wr = nwr;
      }
    }
  }
}
